// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button } from "react-bootstrap";

import { ActionType, IAction, IActionImporterFileUpload, raiseAction } from "actions";
import { IState } from "states/states";
import { JSONreviver, formatTimestamp, getLocalDateTime, isSet } from "utils";
import { FileUploadStatus, IFileImportResultArg } from "dbtypes";
import { ITariffCircuit } from "parameter";
import { ImportFile, ImportFileType } from "graphql/api";
import { toDropdownItems } from "guiutils";

import Dropdown from "view/comp/Dropdown";
import FileUploadBox from "view/comp/FileUploadBox";

import "./Importer.scss";
import cssContent from "content.scss";

enum eImporterFileTypes {
  EDA = "EDA",
  EVU = "EVU",
}

interface IImporterProps {
  isRfidUser: boolean;
  tariffCircuits: ITariffCircuit[] | undefined;
  fileUploads: ImportFile[];
  uploadActive: boolean;
  lastFileUploadError?: string;
}

const mapStateToProps = (state: IState) => {
  // ensure that the (potentially cached) URL is still valid
  const props: IImporterProps = {
    isRfidUser: !!state.site.user?.isRfidUser,
    tariffCircuits: state.parameter?.tariffline,
    fileUploads: state.gui.fileUploads,
    uploadActive: state.scheduler.isFileUploadLoading,
    lastFileUploadError: state.gui.lastFileUploadError,
  };
  return props;
};

interface IImporterState {
  tariffcircuit?: number;
  counter: number;
  fileType: eImporterFileTypes;
}

interface IImporterDispatch {
  onPerformFileUpload: (tariffcircuit: number | undefined, file: File) => void;
  onRefreshFileUploads: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IImporterDispatch = {
    onPerformFileUpload: (tariffcircuit: number | undefined, file: File) => {
      const payload: IActionImporterFileUpload = {
        fileType: ImportFileType.TrendData,
        file,
        args: {
          tariffcircuit,
        },
      };
      dispatch(raiseAction(ActionType.ACT_PERFORM_FILE_UPLOAD_REQ, payload));
    },
    onRefreshFileUploads: () => {
      dispatch(raiseAction(ActionType.ACT_GET_FILEUPLOADS_REQ));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_tariffcircuit_select: {
    id: "Select Tariff Circuit",
  },
  msg_filetype_select: {
    id: "Select Import Type",
  },
  no_info: {
    id: "No Info",
  },
  imported_ems: {
    id: "tariff circuit(s) imported",
  },
  notfound_ems: {
    id: "tariff circuit not found",
  },
  upload_failed: {
    id: "Upload failed",
  },
  import_failed: {
    id: "Import failed",
  },
});

class ImporterComponent extends React.Component<
  IImporterProps & IImporterDispatch & WrappedComponentProps,
  IImporterState
> {
  public state: IImporterState = { counter: 0, fileType: eImporterFileTypes.EDA };

  public componentDidMount() {
    this.props.onRefreshFileUploads();
  }

  public render() {
    const newList = new Array(...this.props.fileUploads);

    // show last entry only!!
    const file = newList.sort((a, b) => a.addedOn - b.addedOn).pop();

    // check if the last entry is initial but older than 60 seconds
    const uploadFailed =
      file?.status === FileUploadStatus.INITIAL && file.addedOn + 60 < new Date().getTime() / 1000;

    // check if the last entry is processing but older than 16 minutes (900 seconds / 15 minutes currently hardcoded in importer-lambda)
    const importFailed =
      file?.status === FileUploadStatus.RECEIVED &&
      file.addedOn + 60 * 16 < new Date().getTime() / 1000;

    // check if last entry is in progress...
    const isImportInProgress =
      isSet(file?.status) &&
      file.status !== FileUploadStatus.PROCESSED &&
      file.status !== FileUploadStatus.ERROR;

    return (
      <>
        <div className="importer">
          {this.renderServerInfo(file, uploadFailed, importFailed)}
          {((!isImportInProgress && !this.props.uploadActive) || uploadFailed || importFailed) &&
            this.renderUploadBox(uploadFailed, importFailed)}
        </div>
      </>
    );
  }

  private renderServerInfo(
    file: ImportFile | undefined,
    uploadFailed: boolean,
    importFailed: boolean,
  ): JSX.Element {
    const result: IFileImportResultArg | undefined = isSet(file?.result)
      ? JSON.parse(file.result, JSONreviver)
      : undefined;

    let maxEMCnt = isSet(result)
      ? (result.notFoundEMs?.length ?? 0) +
        (result.noTariffsFound?.length ?? 0) +
        (result.succeededEMs?.length ?? 0)
      : 0;

    const duration = uploadFailed || importFailed ? 0 : result?.duration ?? 0;

    let notFoundEMCnt = result?.notFoundEMs?.length ?? 0;
    let importedCnt = maxEMCnt - notFoundEMCnt;

    maxEMCnt = isNaN(maxEMCnt) ? 0 : maxEMCnt;
    importedCnt = isNaN(importedCnt) ? 0 : importedCnt;
    notFoundEMCnt = isNaN(notFoundEMCnt) ? 0 : notFoundEMCnt;

    const tooltip_notfound = uploadFailed
      ? this.props.intl.formatMessage(messages.upload_failed)
      : importFailed
      ? this.props.intl.formatMessage(messages.import_failed)
      : isSet(result)
      ? isSet(result.message)
        ? result.message
        : `${result.notFoundEMs?.join(",\n")}`
      : "";
    const importedStuff: string[] = [];

    if (isSet(result)) {
      result.succeededEMs?.forEach((key: any) => {
        importedStuff.push(
          `${key.energyMeter}: Imported ${key.importedEntries} entries to ${key.tariffs.length} tariffs`,
        );
      });
    }

    const tooltip_imported = importedStuff.join(", ");
    const filestatus = uploadFailed
      ? "upload_failed"
      : importFailed
      ? "import_failed"
      : file?.status ?? FileUploadStatus.INITIAL;

    return (
      <>
        {isSet(file) && (
          <>
            <div className="p-0 m-0" style={{ height: "1px" }}></div>
            {this.props.lastFileUploadError && (
              <div className="alert alert-danger m-4 mb-2">
                <FormattedMessage id={this.props.lastFileUploadError ?? "UNKNOWN ERROR"} />
              </div>
            )}
            {uploadFailed && (
              <div className="alert alert-danger m-4">
                <FormattedMessage id="File upload failed. Please try again." />
              </div>
            )}
            {importFailed && (
              <div className="alert alert-danger m-4">
                <FormattedMessage id="File import failed. Please try again." />
              </div>
            )}
            <Button
              variant="secondary"
              className="ms-3 mt-3"
              onClick={() => this.props.onRefreshFileUploads()}
            >
              <FormattedMessage id="Refresh" />
            </Button>
            <table className="ms-3 mt-4">
              <tbody>
                <tr>
                  <td>
                    <div className="me-2">
                      <FormattedMessage id="Status" />:
                    </div>
                  </td>
                  <td className="hstack">
                    <div
                      className="me-2"
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: this.getStatusColor(
                          file?.status as FileUploadStatus,
                          uploadFailed || importFailed,
                        ),
                      }}
                      title={this.props.intl.formatMessage({
                        id: `ImporterStatus_${filestatus}`,
                      })}
                    />
                    <FormattedMessage id={`ImporterStatus_${filestatus}`} />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="me-2">
                      <FormattedMessage id="Uploaded on" />
                    </div>
                  </td>
                  <td className="hstack">
                    <div>{getLocalDateTime(1000 * (file?.addedOn ?? 0))}</div>
                    <div className="fw-bold ms-2">
                      (<FormattedMessage id={`ImporterFileType_${file?.fileType}`} />)
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage id="Info" />:
                  </td>
                  <td>
                    {notFoundEMCnt > 0 && (
                      <>
                        <span
                          title={`${
                            isSet(result)
                              ? tooltip_notfound
                              : this.props.intl.formatMessage(messages.no_info)
                          }`}
                        >{`${
                          isSet(result)
                            ? `${notFoundEMCnt}/${maxEMCnt} ${this.props.intl.formatMessage(
                                messages.notfound_ems,
                              )}`
                            : this.props.intl.formatMessage(messages.no_info)
                        }`}</span>
                        <br />
                      </>
                    )}

                    {importedCnt > 0 ? (
                      <span
                        title={`${
                          isSet(result)
                            ? tooltip_imported
                            : this.props.intl.formatMessage(messages.no_info)
                        }`}
                      >{`${
                        isSet(result)
                          ? `${importedCnt} ${this.props.intl.formatMessage(messages.imported_ems)}`
                          : this.props.intl.formatMessage(messages.no_info)
                      }`}</span>
                    ) : (
                      <span>{tooltip_notfound}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage id="Duration" />:
                  </td>
                  <td>
                    <span>
                      {duration > 0
                        ? `${new Date(duration * 1000).toISOString().substring(11, 19)}`
                        : file?.status !== FileUploadStatus.ERROR &&
                          !uploadFailed &&
                          !importFailed &&
                          isSet(file?.addedOn)
                        ? formatTimestamp(
                            Math.round(
                              file?.status !== FileUploadStatus.PROCESSED &&
                                file?.status !== FileUploadStatus.ERROR
                                ? new Date().getTime() / 1000 - file?.addedOn
                                : result?.duration ?? 0,
                            ),
                          )
                        : "---"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}
      </>
    );
  }

  private renderUploadBox(uploadFailed: boolean, importFailed: boolean) {
    const types = [eImporterFileTypes.EDA, eImporterFileTypes.EVU];

    const items = toDropdownItems(types, "ImporterFileTypes", (name, value) => {
      this.setState({
        fileType: value,
        tariffcircuit: value === eImporterFileTypes.EDA ? undefined : this.state.tariffcircuit,
      });
    });
    const tariffIds = Array.from(Array(this.props.tariffCircuits?.length ?? 0).keys())
      .filter((curId) => this.props.tariffCircuits![curId].webActive)
      .sort((a, b) =>
        this.props.tariffCircuits![a].name.localeCompare(this.props.tariffCircuits![b].name),
      );
    const tariffItems = toDropdownItems(
      tariffIds,
      "tariffCircuit",
      (name, value) => {
        this.setState({ tariffcircuit: value });
      },
      (value) => this.props.tariffCircuits![value].name,
    );
    return (
      <>
        <div className="py-3 ms-4">
          <FormattedMessage id="Select type of import" />
          {": "}
          <Dropdown
            items={items}
            caption={this.props.intl.formatMessage(messages.msg_filetype_select)}
            curItem={<FormattedMessage id={`ImporterFileTypes_${this.state.fileType}`} />}
          />
        </div>
        {this.state.fileType === eImporterFileTypes.EVU && (
          <div className="py-3 ms-4">
            <FormattedMessage id="Select tariff circuit (import destination)" />
            {": "}
            <Dropdown
              items={tariffItems}
              caption={this.props.intl.formatMessage(messages.msg_tariffcircuit_select)}
              curItem={
                isSet(this.state.tariffcircuit)
                  ? this.props.tariffCircuits?.[this.state.tariffcircuit]?.name
                  : this.props.intl.formatMessage(messages.msg_tariffcircuit_select)
              }
            />
          </div>
        )}
        {this.props.uploadActive === true && uploadFailed === false && importFailed === false ? (
          <p>
            <FormattedMessage id="File upload started. Please wait." />
          </p>
        ) : (
          (this.state.fileType === eImporterFileTypes.EDA || isSet(this.state.tariffcircuit)) && (
            <FileUploadBox
              className="py-3 ms-4 h-auto"
              selectedFileChanged={(file) => {
                if (isSet(file)) {
                  this.props.onPerformFileUpload(this.state.tariffcircuit, file);
                }
                this.setState({ counter: this.state.counter + 1 });
              }}
            >
              <>
                <div>
                  <div className="alert alert-warning m-5" role="alert">
                    {this.state.fileType === eImporterFileTypes.EVU && this.renderEVUInfo()}
                    {this.state.fileType === eImporterFileTypes.EDA && this.renderEDAInfo()}
                  </div>
                </div>
              </>
            </FileUploadBox>
          )
        )}
      </>
    );
  }

  private renderEVUInfo() {
    return (
      <>
        <h1>
          <FormattedMessage id="Import load curve data" />
        </h1>
        <p>
          <FormattedMessage id="Imported data will replace (delete!) data with the same timestamp!" />
        </p>
        <p>
          <FormattedMessage id="The file must be in the following format:" />
        </p>
        <div className="d-flex ps-5">
          <ul className="unsortedlist">
            <li>
              <FormattedMessage id="Cloumn header for the timestamps: 'Zeitpunkt'" />
            </li>
            <li>
              <FormattedMessage id="The next column after the timestamp column must contain the power values in kW" />
            </li>
            <li>
              <FormattedMessage id="The timestamps are in Central European (Summer) Time (CET and CEST)" />
            </li>
          </ul>
        </div>
        <p></p>
        <p>
          <FormattedMessage id="This corresponds to the enline trend report 'EVU Format' with 'maximum period energy', the grouping 'date, then tariff circuit', aggregation 'none', filtered by exactly one tariff circuit, and output format 'Microsoft Excel (.xlsx)'." />
        </p>
        <p>
          <FormattedMessage id="Alternatively the file can be formatted in some typical EVU format; " />
          <FormattedMessage id="the following link provides an example of such a file: " />
          <a href="https://www.embyt.com/static/powercalc/Lastgang_embyt.xlsx">
            Lastgang_embyt.xlsx
          </a>
        </p>
        <p></p>
      </>
    );
  }

  private renderEDAInfo() {
    return (
      <>
        <h1>
          <FormattedMessage id="Import EDA data" />
        </h1>
        <p>
          <FormattedMessage id="Imported data will replace (delete!) data with the same timestamp!" />
        </p>
        <p>
          <FormattedMessage id="The file must be in the format of 'EDA Energiedatenreport'." />
        </p>
        <p>
          <FormattedMessage id="The importer will search in the current site for a tariff circuit with exactly one associated energy meter which has the same number as one of the MeteringpointIDs. If found, data is imported to that tariff circuit. This procedure is repeated for each MeteringpointID found in the EDA file." />
        </p>
        <p></p>
      </>
    );
  }

  private getStatusColor(status: FileUploadStatus, failed: boolean): string {
    switch (status) {
      case FileUploadStatus.INITIAL:
        return failed ? cssContent.IMPORTER_ERROR : cssContent.IMPORTER_INITIAL;
      case FileUploadStatus.RECEIVED:
        return failed ? cssContent.IMPORTER_ERROR : cssContent.IMPORTER_PROCESSING;
      case FileUploadStatus.PROCESSED:
        return cssContent.IMPORTER_DONE;
      case FileUploadStatus.ERROR:
        return cssContent.IMPORTER_ERROR;
    }
    return cssContent.CHARGER_COLOR_UNDEFINED;
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ImporterComponent));
